import { Check, ChevronDown, Filter, X } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
// `Input` + `Select` retained: Input is used by FollowersField (numeric
// min/max) + the original City filter dropped at V1 (kept for use when
// city/country split lands in V2). `Select` is still used by SortField.
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useIsMobile } from "@/hooks/use-mobile"
import { countryLabel } from "@/lib/countries"
import { cn } from "@/lib/utils"
import type {
  CandidateFilterOptions,
  CandidateFilters,
  CandidateSort,
  CandidateSortDir,
  CandidateSortField,
} from "@/types/candidates"

/**
 * OKS-17 — Filter rail (UX Spec §Filter rail).
 *
 * Composition (≥640px inline, <640px collapsed behind a `Filters`
 * Sheet button):
 *   - Followers — `Popover` with min/max numeric inputs + Apply
 *   - Country — single-value `Select` over `filter_options.countries`
 *     (exact match against structured `country` column; CTPO V1 lock)
 *   - City — `Input` (ILIKE-contains on the structured `city` column;
 *     commit on blur / Enter)
 *   - Tags — `Command` combobox with multi-select + chip removal
 *   - Sort — `Select` with 4 options
 *   - Clear filters — ghost button (visible iff ≥1 filter or `q` set)
 *
 * Filter mutations write through the `useCandidateListUrl` hook (held
 * by the parent page); each setter emits a partial reload via Inertia
 * and resets `page=1`.
 *
 * **Default-France behavior (CTPO V1 lock):** BE 303-redirects bare
 * `/candidates` to `/candidates?country=France` when the brand has any
 * `country = "France"` candidates. The country `Select`'s "All
 * countries" item passes `""` (empty string preserved in URL) to
 * explicitly opt out of the default. `null` (default state) means
 * "param absent — BE applies default if applicable".
 *
 * Locked testid contract:
 *   - `candidate-list-filter-button` — mobile Sheet trigger
 *   - `candidate-list-filter-followers` — popover trigger
 *   - `candidate-list-filter-followers-{min,max,apply}`
 *   - `candidate-list-filter-country` — country select trigger
 *   - `candidate-list-filter-city` — city input
 *   - `candidate-list-filter-tags` — tags combobox trigger
 *   - `candidate-list-filter-clear` — clear-all button
 *   - `candidate-list-sort` — sort select trigger
 */
interface CandidateFilterRailProps {
  filters: CandidateFilters
  sort: CandidateSort
  filterOptions: CandidateFilterOptions
  onFollowersChange: (min: number | null, max: number | null) => void
  /**
   * Country setter. Pass `null` to clear (BE applies default-France);
   * pass `""` (empty string) to explicitly opt out of default-France
   * (CTPO V1 lock). Pass a non-empty string to filter exactly.
   */
  onCountryChange: (country: string | null) => void
  onCityChange: (city: string | null) => void
  onTagsChange: (tags: string[]) => void
  /** OKS-953 (PRD R5) — multi-select rejection-reason filter. */
  onReasonCodeChange: (codes: string[]) => void
  /** OKS-953 (PRD R5) — multi-select contact-channel filter. */
  onContactChannelChange: (channels: string[]) => void
  onSortChange: (sort: CandidateSort) => void
  onClearAll: () => void
  className?: string
}

export function CandidateFilterRail(props: CandidateFilterRailProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const hasAnyActive = activeFilterCount(props.filters) > 0

  if (isMobile) {
    return (
      <div className={cn("flex flex-wrap items-center gap-2", props.className)}>
        <Sheet>
          <SheetTrigger
            render={
              <Button
                type="button"
                variant="outline"
                data-testid="candidate-list-filter-button"
                className="max-sm:h-11"
              >
                <Filter className="size-4" aria-hidden="true" />
                {t("candidates.list.filters.button")}
                {hasAnyActive && (
                  <Badge variant="secondary" className="ml-1">
                    {activeFilterCount(props.filters)}
                  </Badge>
                )}
              </Button>
            }
          />
          <SheetContent
            side="right"
            className="w-full max-w-sm overflow-y-auto"
          >
            <SheetHeader>
              <SheetTitle>{t("candidates.list.filters.button")}</SheetTitle>
              <SheetDescription className="sr-only">
                {t("candidates.list.filters.active_summary", {
                  count: activeFilterCount(props.filters),
                })}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 flex flex-col gap-6 px-4 pb-6">
              <FollowersField {...props} />
              <CountryField {...props} />
              <CityField {...props} />
              <TagsField {...props} />
              <ReasonCodeField {...props} />
              <ContactChannelField {...props} />
              <SortField {...props} />
              {hasAnyActive && <ClearButton {...props} />}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    )
  }

  // Desktop / tablet — inline rail.
  return (
    <div className={cn("flex flex-wrap items-center gap-3", props.className)}>
      <FollowersField {...props} />
      <CountryField {...props} />
      <CityField {...props} />
      <TagsField {...props} />
      <ReasonCodeField {...props} />
      <ContactChannelField {...props} />
      <SortField {...props} />
      {hasAnyActive && <ClearButton {...props} />}
    </div>
  )
}

// ─── Followers ─────────────────────────────────────────────────────

function FollowersField({
  filters,
  onFollowersChange,
}: CandidateFilterRailProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [min, setMin] = useState(
    filters.followers_min !== null ? String(filters.followers_min) : "",
  )
  const [max, setMax] = useState(
    filters.followers_max !== null ? String(filters.followers_max) : "",
  )

  const hasValue =
    filters.followers_min !== null || filters.followers_max !== null

  function apply(e?: { preventDefault: () => void }) {
    e?.preventDefault()
    const minN = parseIntOrNull(min)
    const maxN = parseIntOrNull(max)
    onFollowersChange(minN, maxN)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            data-testid="candidate-list-filter-followers"
            data-state={hasValue ? "active" : "inactive"}
            className="justify-between"
          >
            <span>{t("candidates.list.filters.followers_label")}</span>
            {hasValue && (
              <Badge variant="secondary" className="ml-2">
                {formatRange(filters.followers_min, filters.followers_max)}
              </Badge>
            )}
            <ChevronDown
              className="text-muted-foreground ml-2 size-4"
              aria-hidden="true"
            />
          </Button>
        }
      />
      <PopoverContent className="w-72" align="start">
        <form className="flex flex-col gap-3" onSubmit={apply}>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="cand-filter-followers-min" className="text-xs">
                {t("candidates.list.filters.followers_min_label")}
              </Label>
              <Input
                id="cand-filter-followers-min"
                type="number"
                inputMode="numeric"
                min={0}
                placeholder={t(
                  "candidates.list.filters.followers_min_placeholder",
                )}
                value={min}
                onChange={(e) => setMin(e.target.value)}
                data-testid="candidate-list-filter-followers-min"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="cand-filter-followers-max" className="text-xs">
                {t("candidates.list.filters.followers_max_label")}
              </Label>
              <Input
                id="cand-filter-followers-max"
                type="number"
                inputMode="numeric"
                min={0}
                placeholder={t(
                  "candidates.list.filters.followers_max_placeholder",
                )}
                value={max}
                onChange={(e) => setMax(e.target.value)}
                data-testid="candidate-list-filter-followers-max"
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="candidate-list-filter-followers-apply"
            className="w-full"
          >
            {t("candidates.list.filters.followers_apply")}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  )
}

function parseIntOrNull(input: string): number | null {
  const trimmed = input.trim()
  if (trimmed === "") return null
  const n = Number.parseInt(trimmed, 10)
  if (!Number.isFinite(n) || n < 0) return null
  return n
}

function formatRange(min: number | null, max: number | null): string {
  if (min !== null && max !== null) return `${min}–${max}`
  if (min !== null) return `≥ ${min}`
  if (max !== null) return `≤ ${max}`
  return ""
}

// ─── Country ───────────────────────────────────────────────────────

/**
 * Country `Select` — exact-match filter against the structured
 * `country` column (CTPO V1 lock). Sentinel `"__all__"` triggers
 * `onCountryChange("")` (explicit empty string preserved in URL) so
 * BE bypasses the default-France behavior. To trigger default-France
 * via FE, pass `null` (param omitted from URL).
 *
 * Locked testid: `candidate-list-filter-country`.
 */
function CountryField({
  filters,
  filterOptions,
  onCountryChange,
}: CandidateFilterRailProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language.startsWith("fr") ? "fr" : "en"

  // Coerce null → empty string display value; empty string → __all__
  // sentinel so the "All countries" item is selectable as the explicit
  // opt-out of default-France.
  const value =
    filters.country === null
      ? "__all__"
      : filters.country === ""
        ? "__all__"
        : filters.country

  // OKS-348 — `filterOptions.countries` is alpha-2 codes; FE resolves
  // display names via `countryLabel(code, locale)` and sorts the
  // dropdown by localized label.
  const sortedOptions = [...filterOptions.countries].sort((a, b) =>
    countryLabel(a, locale).localeCompare(countryLabel(b, locale), locale),
  )

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="cand-filter-country" className="sr-only">
        {t("candidates.list.filters.country_label")}
      </Label>
      <Select
        items={[
          {
            value: "__all__",
            label: t("candidates.list.filters.country_placeholder"),
          },
          ...sortedOptions.map((code) => ({
            value: code,
            label: countryLabel(code, locale),
          })),
        ]}
        value={value}
        onValueChange={(v) => onCountryChange(v === "__all__" ? "" : v)}
      >
        <SelectTrigger
          id="cand-filter-country"
          data-testid="candidate-list-filter-country"
          aria-label={t("candidates.list.filters.country_label")}
          className="min-w-44"
        >
          <SelectValue
            placeholder={t("candidates.list.filters.country_placeholder")}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">
            {t("candidates.list.filters.country_placeholder")}
          </SelectItem>
          {sortedOptions.map((code) => (
            <SelectItem key={code} value={code}>
              {countryLabel(code, locale)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// ─── City ──────────────────────────────────────────────────────────

/**
 * City `Input` — ILIKE-contains match on the structured `city` column
 * (CTPO V1 lock). Commits onBlur or Enter to avoid per-keystroke URL
 * churn (search input owns the debounce pattern; city is a deliberate
 * commit-on-blur simplification).
 *
 * Locked testid: `candidate-list-filter-city`.
 */
function CityField({ filters, onCityChange }: CandidateFilterRailProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState(filters.city ?? "")

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="cand-filter-city" className="sr-only">
        {t("candidates.list.filters.city_label")}
      </Label>
      <Input
        id="cand-filter-city"
        type="text"
        autoComplete="off"
        placeholder={t("candidates.list.filters.city_placeholder")}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const trimmed = value.trim()
          onCityChange(trimmed === "" ? null : trimmed)
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            const trimmed = value.trim()
            onCityChange(trimmed === "" ? null : trimmed)
          }
        }}
        data-testid="candidate-list-filter-city"
        aria-label={t("candidates.list.filters.city_label")}
        className="min-w-40"
      />
    </div>
  )
}

// ─── Tags ──────────────────────────────────────────────────────────

function TagsField({
  filters,
  filterOptions,
  onTagsChange,
}: CandidateFilterRailProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const selected = filters.tags

  function toggle(tag: string) {
    const next = selected.includes(tag)
      ? selected.filter((t) => t !== tag)
      : [...selected, tag]
    onTagsChange(next)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            data-testid="candidate-list-filter-tags"
            data-state={selected.length > 0 ? "active" : "inactive"}
            className="justify-between"
          >
            <span>{t("candidates.list.filters.tags_label")}</span>
            {selected.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {selected.length}
              </Badge>
            )}
            <ChevronDown
              className="text-muted-foreground ml-2 size-4"
              aria-hidden="true"
            />
          </Button>
        }
      />
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t("candidates.list.filters.tags_search_placeholder")}
          />
          <CommandList>
            <CommandEmpty>
              {t("candidates.list.filters.tags_empty")}
            </CommandEmpty>
            <CommandGroup>
              {filterOptions.tags.map((tag) => {
                const isSelected = selected.includes(tag)
                return (
                  <CommandItem
                    key={tag}
                    value={tag}
                    onSelect={() => toggle(tag)}
                    aria-selected={isSelected}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                    {tag}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected.length > 0 && (
          <div className="border-t p-2">
            <div className="flex flex-wrap gap-1">
              {selected.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => toggle(tag)}
                    aria-label={t("candidates.list.filters.tags_remove_aria", {
                      tag,
                    })}
                    className="hover:bg-muted -mr-0.5 rounded-sm p-0.5"
                  >
                    <X className="size-3" aria-hidden="true" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

// ─── OKS-953 — Motif de refus / Canal de contact ───────────────────

/**
 * Both fields copy the shipped `TagsField` pattern verbatim — Popover +
 * Command + Check + a selected-chip footer. Same primitives, same keyboard
 * and a11y behaviour, no new `ui/**` primitive, and the shadcn generator was
 * not used.
 *
 * The option lists come from `filter_options` and are UNCOUNTED by design
 * (see `Candidates::FilterOptionsDigest`): `statuses` is the only counted
 * facet, because the tab strip renders every count and these pickers are not
 * compared against each other.
 */
function ReasonCodeField({
  filters,
  filterOptions,
  onReasonCodeChange,
}: CandidateFilterRailProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const selected = filters.reason_code

  function toggle(code: string) {
    onReasonCodeChange(
      selected.includes(code)
        ? selected.filter((c) => c !== code)
        : [...selected, code],
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            data-testid="candidate-list-filter-reason-code"
            data-state={selected.length > 0 ? "active" : "inactive"}
            className="justify-between"
          >
            <span>{t("candidates.list.filters.reason_code_label")}</span>
            {selected.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {selected.length}
              </Badge>
            )}
            <ChevronDown
              className="text-muted-foreground ml-2 size-4"
              aria-hidden="true"
            />
          </Button>
        }
      />
      <PopoverContent className="w-72 p-0" align="start">
        {/*
          The hint is persistent, not a tooltip: this filter matches only
          CURRENTLY rejected candidates, and someone filtering by a reason and
          seeing fewer rows than they expect needs the rule in front of them,
          not one hover away (PRD R5).
        */}
        <p className="text-muted-foreground border-b px-3 py-2 text-xs">
          {t("candidates.list.filters.reason_code_hint")}
        </p>
        <Command>
          <CommandInput
            placeholder={t("candidates.list.filters.reason_code_placeholder")}
          />
          <CommandList>
            <CommandEmpty>
              {t("candidates.list.filters.reason_code_empty")}
            </CommandEmpty>
            <CommandGroup>
              {filterOptions.reason_codes.map((code) => {
                const isSelected = selected.includes(code)
                return (
                  <CommandItem
                    key={code}
                    value={code}
                    onSelect={() => toggle(code)}
                    aria-selected={isSelected}
                    data-testid={`candidate-list-filter-reason-code-${code}`}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                    {/* Reuses the shipped OKS-948/952 reason labels. */}
                    {t(`candidates.reason_code.${code}`)}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function ContactChannelField({
  filters,
  filterOptions,
  onContactChannelChange,
}: CandidateFilterRailProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const selected = filters.contact_channel

  function toggle(channel: string) {
    onContactChannelChange(
      selected.includes(channel)
        ? selected.filter((c) => c !== channel)
        : [...selected, channel],
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            data-testid="candidate-list-filter-contact-channel"
            data-state={selected.length > 0 ? "active" : "inactive"}
            className="justify-between"
          >
            <span>{t("candidates.list.filters.contact_channel_label")}</span>
            {selected.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {selected.length}
              </Badge>
            )}
            <ChevronDown
              className="text-muted-foreground ml-2 size-4"
              aria-hidden="true"
            />
          </Button>
        }
      />
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t(
              "candidates.list.filters.contact_channel_placeholder",
            )}
          />
          <CommandList>
            <CommandEmpty>
              {t("candidates.list.filters.reason_code_empty")}
            </CommandEmpty>
            <CommandGroup>
              {filterOptions.contact_channels.map((channel) => {
                const isSelected = selected.includes(channel)
                return (
                  <CommandItem
                    key={channel}
                    value={channel}
                    onSelect={() => toggle(channel)}
                    aria-selected={isSelected}
                    data-testid={`candidate-list-filter-contact-channel-${channel}`}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                    {/*
                      Reuses the shipped OKS-951 channel labels. The path is
                      `contact.channel.OPTIONS.{channel}` — verified against the
                      YAML, not guessed: `contact.channel` itself holds
                      `legend`, and a label minted one level up would have been
                      a silent duplicate of an existing string.
                    */}
                    {t(`candidates.contact.channel.options.${channel}`)}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// ─── Sort ──────────────────────────────────────────────────────────

const SORT_OPTIONS: readonly {
  value: string
  field: CandidateSortField
  dir: CandidateSortDir
  labelKey: string
}[] = [
  {
    value: "created_at:desc",
    field: "created_at",
    dir: "desc",
    labelKey: "candidates.list.sort.created_at_desc",
  },
  {
    value: "created_at:asc",
    field: "created_at",
    dir: "asc",
    labelKey: "candidates.list.sort.created_at_asc",
  },
  {
    value: "approx_followers:desc",
    field: "approx_followers",
    dir: "desc",
    labelKey: "candidates.list.sort.approx_followers_desc",
  },
  {
    value: "approx_followers:asc",
    field: "approx_followers",
    dir: "asc",
    labelKey: "candidates.list.sort.approx_followers_asc",
  },
]

function SortField({ sort, onSortChange }: CandidateFilterRailProps) {
  const { t } = useTranslation()
  const value = `${sort.field}:${sort.dir}`

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="cand-filter-sort" className="sr-only">
        {t("candidates.list.sort.label")}
      </Label>
      <Select
        items={SORT_OPTIONS.map((opt) => ({
          value: opt.value,
          label: t(opt.labelKey),
        }))}
        value={value}
        onValueChange={(v) => {
          const opt = SORT_OPTIONS.find((o) => o.value === v)
          if (opt) onSortChange({ field: opt.field, dir: opt.dir })
        }}
      >
        <SelectTrigger
          id="cand-filter-sort"
          data-testid="candidate-list-sort"
          aria-label={t("candidates.list.sort.label")}
          className="min-w-44"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// ─── Clear all ─────────────────────────────────────────────────────

function ClearButton({ onClearAll }: CandidateFilterRailProps) {
  const { t } = useTranslation()
  return (
    <Button
      type="button"
      variant="ghost"
      data-testid="candidate-list-filter-clear"
      onClick={onClearAll}
    >
      {t("candidates.list.filters.clear")}
    </Button>
  )
}

// ─── helpers ───────────────────────────────────────────────────────

export function activeFilterCount(filters: CandidateFilters): number {
  let n = 0
  if (filters.q) n++
  if (filters.status.length > 0) n++
  if (filters.followers_min !== null || filters.followers_max !== null) n++
  // Phase 4 F3 fix — only count country as "active" when the user has
  // a real (non-empty) value. The empty-string `""` sentinel is the
  // "user cleared default-France" signal which `ActiveFilterChips`
  // intentionally renders no chip for; counting it here would surface
  // "1 active" on mobile with an empty rail (mismatch).
  if (filters.country !== null && filters.country !== "") n++
  if (filters.city) n++
  if (filters.tags.length > 0) n++
  // OKS-953 — each new filter counts ONCE regardless of how many values
  // are selected, matching how `status` and `tags` are counted above.
  if (filters.reason_code.length > 0) n++
  if (filters.contact_channel.length > 0) n++
  return n
}
